import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Upload } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const STATE = { none: 'Ainda não está no Mural', awaiting_logo: 'Falta o logótipo', awaiting_approval: 'Logótipo recebido — a aguardar revisão da equipa Trabadju', published: 'Publicado no Mural de Confiança' };

export default function EmployerLogoUpload() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const load = () => api.get('/employer/pioneer-logo').then((r) => setInfo(r.data)).catch(() => setInfo(false));
  useEffect(() => { if (user?.role === 'employer') load(); }, [user?.role]);
  if (user && user.role !== 'employer') return <p className="p-10 text-center text-slate-600">Esta página é só para contas de empresa.</p>;
  const send = async () => {
    const fd = new FormData(); fd.append('logo', file); fd.append('consent', consent || info?.consent ? 'true' : 'false');
    setBusy(true);
    try { await api.post('/employer/pioneer-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Logótipo enviado — obrigado!'); setFile(null); setInputKey((k) => k + 1); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Não foi possível enviar.'); } finally { setBusy(false); }
  };
  const needConsent = info && !info.consent;
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="employer-logo-page">
      <Card className="mx-auto max-w-lg p-6">
        <h1 className="text-2xl font-bold text-slate-900">Logótipo para o Mural de Confiança</h1>
        <p className="mt-1 text-sm text-slate-600">{user?.company_name}</p>
        {info === null ? <p className="mt-4 text-sm text-slate-500">A carregar…</p> : info === false ? <p className="mt-4 text-sm text-red-600">Não foi possível carregar.</p> : <>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700" data-testid="employer-logo-state">
            {info.mural_state === 'published' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}{STATE[info.mural_state]}
          </p>
          <div className="mt-5 space-y-3">
            <label htmlFor="emp-logo" className="block text-sm font-semibold text-slate-700">{info.has_logo ? 'Trocar o logótipo' : 'Carregar o logótipo'} (PNG ou SVG · também JPG/WEBP · máx. 2 MB)</label>
            <input key={inputKey} id="emp-logo" type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:font-semibold" data-testid="employer-logo-input" />
            {preview && <img src={preview} alt="" className="h-16 object-contain" data-testid="employer-logo-preview" />}
            {needConsent && <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4" data-testid="employer-logo-consent" />Autorizo o Trabadju.cv a mostrar o nosso logo no Mural de Confiança.</label>}
            <Button onClick={send} disabled={busy || !file || (needConsent && !consent)} className="w-full" data-testid="employer-logo-submit"><Upload className="h-4 w-4 mr-2" />{busy ? 'A enviar…' : 'Enviar logótipo'}</Button>
            <p className="text-xs text-slate-500">O logo só aparece no site depois de revisto pela equipa Trabadju.</p>
          </div>
        </>}
        <Link to="/dashboard" className="mt-6 inline-block text-sm text-blue-700 underline" data-testid="employer-logo-back">Voltar ao painel</Link>
      </Card>
    </div>
  );
}
